/**
 * nova-geometry.ts — layout constants + SVG path builders.
 * Every number is transcribed 1:1 from nova.js (the canonical generator),
 * which the SwiftUI kit also mirrors. Change SPEC.md first, then all ports.
 */

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
  r: number;
}

/** Canvas — sized to fit the whole figure plus raised arms / camera prop.
 *  BOTTOM-CLIP FIX: the body bottom sits at head.y(110)+head.h(170)+gap(22)
 *  +body.h(170) = 472, but the height was 460 — and the outer <svg> clips
 *  to its viewBox — so Nova was cropped ~12px at her base everywhere she
 *  renders. Height is 480 now (8px clearance below the body); width and
 *  content coordinates are unchanged, so the fix is purely "show her feet." */
export const NOVA_VIEWBOX = { w: 460, h: 480, xOffset: 50 } as const;

export interface NovaLayout {
  head: Box;
  bezel: Box;
  screen: Box;
  body: Box;
  chest: Box;
  screenCx: number;
  screenCy: number;
  eyeW: number;
  eyeH: number;
  eyeGapHalf: number;
}

/** Compute the character layout. `bodyGapShrink` hunches her (Sad). */
export function novaLayout(bodyGapShrink = 0): NovaLayout {
  const head: Box = { w: 240, h: 170, x: 60, y: 110, r: 36 };

  const bezelInset = 22;
  const bezel: Box = {
    x: head.x + bezelInset,
    y: head.y + bezelInset,
    w: head.w - bezelInset * 2,
    h: head.h - bezelInset * 2,
    r: head.r - 8,
  };

  const screenInset = 28;
  const screen: Box = {
    x: head.x + screenInset,
    y: head.y + screenInset,
    w: head.w - screenInset * 2,
    h: head.h - screenInset * 2,
    r: head.r - 14,
  };

  const gap = 22 - bodyGapShrink;
  const body: Box = { w: 260, h: 170, x: 50, y: head.y + head.h + gap, r: 36 };

  const chestInset = 22;
  const chest: Box = {
    x: body.x + chestInset,
    y: body.y + chestInset,
    w: body.w - chestInset * 2,
    h: body.h - chestInset * 2,
    r: body.r - 10,
  };

  const eyeH = screen.h * 0.55;
  return {
    head,
    bezel,
    screen,
    body,
    chest,
    screenCx: screen.x + screen.w / 2,
    screenCy: screen.y + screen.h / 2,
    eyeH,
    eyeW: eyeH * 0.78,
    eyeGapHalf: screen.w * 0.18,
  };
}

const f = (n: number) => n.toFixed(2);

/** Isoceles triangle with rounded corners, optionally tilted around its center. */
export function softTrianglePath(
  cx: number,
  cy: number,
  w: number,
  h: number,
  orientation: "up" | "down" = "up",
  tiltDeg = 0,
): string {
  let pts: Array<[number, number]> =
    orientation === "up"
      ? [
          [cx, cy - h / 2],
          [cx + w / 2, cy + h / 2],
          [cx - w / 2, cy + h / 2],
        ]
      : [
          [cx, cy + h / 2],
          [cx - w / 2, cy - h / 2],
          [cx + w / 2, cy - h / 2],
        ];

  if (tiltDeg) {
    const rad = (tiltDeg * Math.PI) / 180;
    const cs = Math.cos(rad);
    const sn = Math.sin(rad);
    pts = pts.map(([x, y]) => {
      const dx = x - cx;
      const dy = y - cy;
      return [cx + dx * cs - dy * sn, cy + dx * sn + dy * cs];
    });
  }

  const r = 6; // corner radius
  const n = pts.length;
  let d = "";
  for (let i = 0; i < n; i++) {
    const prev = pts[(i - 1 + n) % n];
    const curr = pts[i];
    const next = pts[(i + 1) % n];
    const v1x = prev[0] - curr[0];
    const v1y = prev[1] - curr[1];
    const v2x = next[0] - curr[0];
    const v2y = next[1] - curr[1];
    const l1 = Math.hypot(v1x, v1y);
    const l2 = Math.hypot(v2x, v2y);
    const t = Math.min(r, l1 * 0.4, l2 * 0.4);
    const p1 = [curr[0] + (v1x / l1) * t, curr[1] + (v1y / l1) * t];
    const p2 = [curr[0] + (v2x / l2) * t, curr[1] + (v2y / l2) * t];
    d += `${i === 0 ? "M" : "L"} ${f(p1[0])} ${f(p1[1])} `;
    d += `Q ${f(curr[0])} ${f(curr[1])} ${f(p2[0])} ${f(p2[1])} `;
  }
  return d + "Z";
}

/** Teardrop — pinched top, round bulb at the bottom (Sad). */
export function teardropPath(cx: number, cy: number, w: number, h: number): string {
  const r = w / 2;
  const apexY = cy - h / 2;
  const baseY = cy + h / 2 - r;
  const d = baseY - apexY;
  const sinA = r / d;
  const cosA = Math.sqrt(Math.max(0, 1 - sinA * sinA));
  const tx = r * cosA;
  const ty = -r * sinA;
  return `M ${f(cx)} ${f(apexY)}
          Q ${f(cx - tx * 0.55)} ${f(apexY + (baseY + ty - apexY) * 0.55)} ${f(cx - tx)} ${f(baseY + ty)}
          A ${f(r)} ${f(r)} 0 1 0 ${f(cx + tx)} ${f(baseY + ty)}
          Q ${f(cx + tx * 0.55)} ${f(apexY + (baseY + ty - apexY) * 0.55)} ${f(cx)} ${f(apexY)} Z`;
}

/** Two-lobed heart (Love). */
export function heartPath(cx: number, cy: number, w: number, h: number): string {
  const topY = cy - h / 2 + w * 0.18;
  const botY = cy + h / 2;
  return `M ${cx} ${botY}
          C ${cx - w * 0.62} ${cy + h * 0.05} ${cx - w * 0.55} ${topY - w * 0.1} ${cx - w * 0.25} ${topY}
          C ${cx - w * 0.08} ${topY + w * 0.02} ${cx} ${topY + w * 0.16} ${cx} ${topY + w * 0.22}
          C ${cx} ${topY + w * 0.16} ${cx + w * 0.08} ${topY + w * 0.02} ${cx + w * 0.25} ${topY}
          C ${cx + w * 0.55} ${topY - w * 0.1} ${cx + w * 0.62} ${cy + h * 0.05} ${cx} ${botY} Z`;
}

/** Tight caret ^ (Laughing). */
export function caretPath(cx: number, cy: number, w: number, h: number): string {
  return `M ${cx - w / 2} ${cy + h / 2} L ${cx} ${cy - h / 2} L ${cx + w / 2} ${cy + h / 2}`;
}

/** Spinning spiral (Dizzy). */
export function spiralPath(cx: number, cy: number, r: number): string {
  return `M ${cx + r} ${cy}
          A ${r} ${r} 0 1 1 ${cx} ${cy - r}
          A ${r * 0.62} ${r * 0.62} 0 1 0 ${cx + r * 0.62} ${cy}
          A ${r * 0.3} ${r * 0.3} 0 1 1 ${cx} ${cy - r * 0.3}`;
}

/** Four-point sparkle star (Starstruck). */
export function star4Path(cx: number, cy: number, r: number): string {
  const q = r * 0.24;
  return `M ${cx} ${cy - r} Q ${cx + q * 0.4} ${cy - q} ${cx + r} ${cy}
          Q ${cx + q * 0.4} ${cy + q} ${cx} ${cy + r}
          Q ${cx - q * 0.4} ${cy + q} ${cx - r} ${cy}
          Q ${cx - q * 0.4} ${cy - q} ${cx} ${cy - r} Z`;
}

/** Upward smiling arc (Happy, Wink's closed eye). */
export function upArcPath(cx: number, cy: number, w: number, h: number): string {
  return `M ${cx - w / 2} ${cy} Q ${cx} ${cy - h} ${cx + w / 2} ${cy}`;
}

/** Squeezed-shut downward arc (Crying-laughing). */
export function downArcPath(cx: number, cy: number, w: number, h: number): string {
  return `M ${cx - w / 2} ${cy - h / 2} Q ${cx} ${cy + h} ${cx + w / 2} ${cy - h / 2}`;
}
